import React, { useState } from 'react';
import './App.css';

function App() {
  const [activeSection, setActiveSection] = useState('introduction');
  const [copiedCode, setCopiedCode] = useState(null);
  const [queryParams, setQueryParams] = useState({
    baseUrl: 'http://127.0.0.1:8787',
    page: '1',
    sortOrder: '0',
    keywords: '',
    keywordsType: 'all',
    keywordsContain: '3',
    aircraft: 'all',
    airline: 'all',
    country: 'all',
    year: 'all',
    photographer: 'all',
    width: '',
    height: ''
  });

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const CodeBlock = ({ code, language, id }) => (
    <div className="relative">
      <div className="flex items-center justify-between bg-gray-800 px-4 py-2 rounded-t-md">
        <span className="text-xs text-gray-400 uppercase font-semibold">{language}</span>
        <button
          onClick={() => copyToClipboard(code, id)}
          className="text-xs text-gray-400 hover:text-white transition-colors"
        >
          {copiedCode === id ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <pre className="bg-gray-900 text-gray-100 p-4 rounded-b-md overflow-x-auto text-sm">
        <code>{code}</code>
      </pre>
    </div>
  );

  const Badge = ({ children, variant = 'default' }) => {
    const colors = {
      required: 'bg-red-100 text-red-800',
      optional: 'bg-blue-100 text-blue-800',
      get: 'bg-green-100 text-green-800',
      default: 'bg-gray-100 text-gray-800'
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[variant]}`}>
        {children}
      </span>
    );
  };

  const ParamRow = ({ name, type, required, defaultValue, description, examples }) => (
    <tr className="border-b border-gray-200 hover:bg-gray-50">
      <td className="px-6 py-4">
        <div className="flex flex-col gap-1">
          <code className="text-sm font-semibold text-indigo-600">{name}</code>
          <Badge variant={required ? 'required' : 'optional'}>
            {required ? 'Required' : 'Optional'}
          </Badge>
        </div>
      </td>
      <td className="px-6 py-4">
        <code className="text-sm text-gray-700">{type}</code>
      </td>
      <td className="px-6 py-4">
        <code className="text-sm text-gray-600">{defaultValue || '-'}</code>
      </td>
      <td className="px-6 py-4 text-sm text-gray-700">
        <div>{description}</div>
        {examples && (
          <div className="mt-2 text-xs text-gray-500">
            <span className="font-semibold">Examples: </span>
            {examples.map((ex, i) => (
              <code key={i} className="bg-gray-100 px-1 py-0.5 rounded mx-1">{ex}</code>
            ))}
          </div>
        )}
      </td>
    </tr>
  );

  return (
    <div className="flex min-h-screen bg-white">
      {/* Sidebar */}
      <aside className="w-64 bg-gray-900 text-gray-100 fixed h-full overflow-y-auto">
        <div className="p-6">
          <h1 className="text-xl font-bold mb-2">JetPhotos API</h1>
          <p className="text-xs text-gray-400">Unofficial Documentation</p>
        </div>

        <nav className="px-4 pb-6">
          <div className="space-y-1">
            <button
              onClick={() => setActiveSection('introduction')}
              className={`w-full text-left px-3 py-2 rounded text-sm ${
                activeSection === 'introduction' ? 'bg-indigo-600 text-white' : 'text-gray-300 hover:bg-gray-800'
              }`}
            >
              Introduction
            </button>
            <button
              onClick={() => setActiveSection('quickstart')}
              className={`w-full text-left px-3 py-2 rounded text-sm ${
                activeSection === 'quickstart' ? 'bg-indigo-600 text-white' : 'text-gray-300 hover:bg-gray-800'
              }`}
            >
              Quick Start
            </button>
            <button
              onClick={() => setActiveSection('authentication')}
              className={`w-full text-left px-3 py-2 rounded text-sm ${
                activeSection === 'authentication' ? 'bg-indigo-600 text-white' : 'text-gray-300 hover:bg-gray-800'
              }`}
            >
              Authentication
            </button>

            <div className="pt-4 pb-2">
              <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                API Reference
              </h3>
            </div>

            <button
              onClick={() => setActiveSection('endpoints')}
              className={`w-full text-left px-3 py-2 rounded text-sm ${
                activeSection === 'endpoints' ? 'bg-indigo-600 text-white' : 'text-gray-300 hover:bg-gray-800'
              }`}
            >
              Search Endpoint
            </button>
            <button
              onClick={() => setActiveSection('parameters')}
              className={`w-full text-left px-3 py-2 rounded text-sm ${
                activeSection === 'parameters' ? 'bg-indigo-600 text-white' : 'text-gray-300 hover:bg-gray-800'
              }`}
            >
              Query Parameters
            </button>
            <button
              onClick={() => setActiveSection('responses')}
              className={`w-full text-left px-3 py-2 rounded text-sm ${
                activeSection === 'responses' ? 'bg-indigo-600 text-white' : 'text-gray-300 hover:bg-gray-800'
              }`}
            >
              Response Format
            </button>
            <button
              onClick={() => setActiveSection('errors')}
              className={`w-full text-left px-3 py-2 rounded text-sm ${
                activeSection === 'errors' ? 'bg-indigo-600 text-white' : 'text-gray-300 hover:bg-gray-800'
              }`}
            >
              Error Handling
            </button>

            <div className="pt-4 pb-2">
              <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Tools
              </h3>
            </div>

            <button
              onClick={() => setActiveSection('querybuilder')}
              className={`w-full text-left px-3 py-2 rounded text-sm ${
                activeSection === 'querybuilder' ? 'bg-indigo-600 text-white' : 'text-gray-300 hover:bg-gray-800'
              }`}
            >
              Query Builder
            </button>

            <div className="pt-4 pb-2">
              <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Deployment
              </h3>
            </div>

            <button
              onClick={() => setActiveSection('hosting')}
              className={`w-full text-left px-3 py-2 rounded text-sm ${
                activeSection === 'hosting' ? 'bg-indigo-600 text-white' : 'text-gray-300 hover:bg-gray-800'
              }`}
            >
              Hosting
            </button>

            <div className="pt-4 pb-2">
              <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Resources
              </h3>
            </div>

            <button
              onClick={() => setActiveSection('sourcecode')}
              className={`w-full text-left px-3 py-2 rounded text-sm ${
                activeSection === 'sourcecode' ? 'bg-indigo-600 text-white' : 'text-gray-300 hover:bg-gray-800'
              }`}
            >
              Source Code
            </button>

            <a
              href="https://github.com/roowus/Jetphotos-API"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-left px-3 py-2 rounded text-sm text-gray-300 hover:bg-gray-800"
            >
              GitHub Repository ↗
            </a>
          </div>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="ml-64 flex-1 p-8">
        <div className="max-w-4xl">

          {/* Introduction */}
          {activeSection === 'introduction' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Introduction</h2>
              <div className="prose prose-lg">
                <p className="text-gray-700 mb-4">
                  The JetPhotos API is an unofficial, <strong>100% free</strong>, self-hosted API for{' '}
                  <a href="https://www.jetphotos.com" target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline">JetPhotos.com</a>{' '}
                  aircraft photo search. Run it on your own computer or VPS with <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">npm start</code> and
                  get structured JSON for registration, aircraft, airline, and photographer searches — no API keys and no paid scraper services required.
                </p>

                <div className="bg-green-50 border-l-4 border-green-500 p-4 my-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-green-700 font-semibold">No keys, no cost, no proxy</p>
                      <p className="text-sm text-green-700 mt-1">
                        The API runs on the free Wrangler (workerd) runtime from your own network. No Cloudflare account
                        plan, no scraper SaaS, and no authentication tokens are needed.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700">
                        This is an unofficial API and is not affiliated with or endorsed by JetPhotos.com.
                      </p>
                    </div>
                  </div>
                </div>

                <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Base URL</h3>
                <p className="text-gray-700 mb-3">
                  Because you self-host the API, the base URL is wherever you run it. The local default is:
                </p>
                <div className="space-y-2">
                  <div className="bg-gray-50 p-4 rounded-md">
                    <p className="text-sm text-gray-600 mb-1">Local self-host (recommended)</p>
                    <code className="text-sm text-indigo-600">http://127.0.0.1:8787</code>
                  </div>
                </div>
                <p className="text-gray-700 mt-3">
                  Need a public URL from your home machine? Put a free tunnel (e.g.{' '}
                  <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">cloudflared tunnel</code>) or a reverse proxy in
                  front of port <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">8787</code> and point your app at that URL.
                </p>

                <div className="mt-4 bg-gray-50 border border-gray-200 rounded-md p-4">
                  <p className="text-sm font-semibold text-gray-900 mb-2">How the free path works</p>
                  <code className="text-sm text-gray-700 block">
                    Client → this API on your PC/VPS (Wrangler/workerd) → JetPhotos HTML → JSON
                  </code>
                </div>

                <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-500 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        A Cloudflare Workers <em>edge</em> deploy (<code>*.workers.dev</code>) is{' '}
                        <strong>often blocked</strong> by JetPhotos with a <code>403</code>. Self-hosting locally is the
                        reliable free path. See the <strong>Hosting</strong> section for details.
                      </p>
                    </div>
                  </div>
                </div>

                <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Features</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-indigo-600 mr-2">•</span>
                    <span className="text-gray-700"><strong>100% free &amp; self-hosted</strong> — no API keys, no paid scrapers, no account required</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-indigo-600 mr-2">•</span>
                    <span className="text-gray-700">Search aircraft photos by registration, type, airline, location, and more</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-indigo-600 mr-2">•</span>
                    <span className="text-gray-700">Filter by photographer, date, and image dimensions</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-indigo-600 mr-2">•</span>
                    <span className="text-gray-700">Paginated results with flexible sorting options</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-indigo-600 mr-2">•</span>
                    <span className="text-gray-700">Clean JSON responses with photo URLs, metadata, statistics, and a <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">meta</code> block</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-indigo-600 mr-2">•</span>
                    <span className="text-gray-700">Built-in response caching (6h default) so you can be polite to JetPhotos</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-indigo-600 mr-2">•</span>
                    <span className="text-gray-700">Open CORS (<code className="bg-gray-100 px-1 py-0.5 rounded text-sm">Access-Control-Allow-Origin: *</code>) for browser apps</span>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* Quick Start */}
          {activeSection === 'quickstart' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Quick Start</h2>

              <p className="text-gray-700 mb-6">
                The API is self-hosted, so getting started means cloning and running it locally. It takes about a minute.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">1. Clone &amp; run</h3>
              <CodeBlock
                language="bash"
                id="quickstart-install"
                code={`git clone https://github.com/roowus/Jetphotos-API.git
cd Jetphotos-API
npm install
npm start`}
              />
              <p className="text-gray-700 mt-3">
                Leave that terminal running. The API now listens at{' '}
                <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">http://127.0.0.1:8787</code>.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">2. Health check</h3>
              <CodeBlock
                language="bash"
                id="quickstart-health"
                code={`curl -s http://127.0.0.1:8787/health | jq .`}
              />
              <CodeBlock
                language="JSON"
                id="quickstart-health-resp"
                code={`{
  "ok": true,
  "free": true,
  "mode": "workerd-direct"
}`}
              />

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">3. Run a search</h3>
              <CodeBlock
                language="curl"
                id="quickstart-curl"
                code={`curl -s "http://127.0.0.1:8787/?page=1&sort-order=0&keywords=Boeing%20747&keywords-type=aircraft&keywords-contain=3" \\
  | jq '{count, free: .meta.free, mode: .meta.mode, first: .photos[0].registration}'`}
              />

              <div className="mt-6 bg-green-50 border-l-4 border-green-500 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-green-700 font-semibold">Pass criteria</p>
                    <p className="text-sm text-green-700 mt-1">
                      A working setup returns a real photo count with <code>free: true</code> and <code>mode: "workerd-direct"</code>.
                    </p>
                  </div>
                </div>
              </div>

              <CodeBlock
                language="JSON"
                id="quickstart-pass"
                code={`{
  "count": 52,
  "free": true,
  "mode": "workerd-direct",
  "first": "N452PA"
}`}
              />

              <p className="text-gray-700 mt-4">
                Prefer an automated check? The repo ships a smoke test:
              </p>
              <CodeBlock
                language="bash"
                id="quickstart-smoke"
                code={`npm run test:api`}
              />

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">4. Use it from your app</h3>
              <div className="mt-4">
                <h4 className="text-base font-semibold text-gray-900 mb-2">JavaScript</h4>
                <CodeBlock
                  language="JavaScript"
                  id="quickstart-js"
                  code={`const base = 'http://127.0.0.1:8787';
const params = new URLSearchParams({
  page: '1',
  'sort-order': '0',
  keywords: 'Boeing 747',
  'keywords-type': 'aircraft',
  'keywords-contain': '3'
});

fetch(\`\${base}/?\${params}\`)
  .then(res => res.json())
  .then(data => {
    console.log(\`Found \${data.count} photos (free: \${data.meta.free})\`);
    data.photos.forEach(p => console.log(\`\${p.registration} - \${p.aircraftType}\`));
  });`}
                />
              </div>

              <div className="mt-6">
                <h4 className="text-base font-semibold text-gray-900 mb-2">Python</h4>
                <CodeBlock
                  language="Python"
                  id="quickstart-python"
                  code={`import requests

base = "http://127.0.0.1:8787"
params = {
    "page": 1,
    "sort-order": 0,
    "keywords": "Boeing 747",
    "keywords-type": "aircraft",
    "keywords-contain": 3
}

data = requests.get(f"{base}/", params=params).json()
print(f"Found {data['count']} photos (free: {data['meta']['free']})")
for p in data["photos"]:
    print(f"{p['registration']} - {p['aircraftType']}")`}
                />
              </div>

              <div className="mt-8 bg-yellow-50 border-l-4 border-yellow-500 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      If <code>/health</code> works but search returns a <code>403</code>, your current IP is blocked by
                      JetPhotos/Cloudflare. Try a different network (home residential works best), a different VPS region,
                      or a tunnel from home. See the <strong>Hosting</strong> section.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Authentication */}
          {activeSection === 'authentication' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Authentication</h2>

              <div className="bg-green-50 border-l-4 border-green-500 p-4 my-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-green-700 font-semibold">
                      No authentication required!
                    </p>
                    <p className="text-sm text-green-700 mt-1">
                      The JetPhotos API is completely open and does not require API keys or authentication tokens.
                    </p>
                  </div>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Being a good citizen</h3>
              <p className="text-gray-700 mb-4">
                There is no app-level rate limiter — you run the API yourself. The real constraint is JetPhotos itself:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2">•</span>
                  <span><strong>Caching is built in:</strong> successful responses are cached for 6&nbsp;hours by default (<code className="bg-gray-100 px-1 py-0.5 rounded text-sm">CACHE_TTL_SECONDS</code>), so repeat queries hit your cache, not JetPhotos.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2">•</span>
                  <span><strong>Avoid tight loops:</strong> JetPhotos is Cloudflare-protected. Aggressive polling from one IP can earn a <code>403</code> block.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2">•</span>
                  <span><strong>Review the terms:</strong> this is an unofficial API. Check JetPhotos' terms before any heavy or production use.</span>
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">CORS</h3>
              <p className="text-gray-700">
                Cross-Origin Resource Sharing (CORS) is enabled by default, allowing you to make requests from
                browser-based applications.
              </p>
            </div>
          )}

          {/* Endpoints */}
          {activeSection === 'endpoints' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Endpoints</h2>

              <p className="text-gray-700 mb-6">
                Examples below use the local base URL <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">http://127.0.0.1:8787</code>.
                Replace it with your own self-hosted URL (VPS host, or a tunnel) as needed.
              </p>

              <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <Badge variant="get">GET</Badge>
                  <code className="text-lg font-mono text-gray-900">/health</code>
                </div>
                <p className="text-gray-700 mb-3">
                  Lightweight liveness check. Use it to confirm your instance is up before running searches.
                </p>
                <CodeBlock
                  language="bash"
                  id="endpoint-health"
                  code={`curl -s http://127.0.0.1:8787/health | jq .`}
                />
                <CodeBlock
                  language="JSON"
                  id="endpoint-health-resp"
                  code={`{
  "ok": true,
  "free": true,
  "mode": "workerd-direct"
}`}
                />
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <Badge variant="get">GET</Badge>
                  <code className="text-lg font-mono text-gray-900">/</code>
                  <span className="text-sm text-gray-500">or <code className="text-gray-700">/search</code></span>
                </div>
                <p className="text-gray-700">
                  Main search endpoint for querying aircraft photos. <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">/</code> and{' '}
                  <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">/search</code> are aliases. Accepts the query
                  parameters described in the next section to filter and sort results.
                </p>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Request Example</h3>
              <CodeBlock
                language="HTTP"
                id="endpoint-http"
                code={`GET /?page=1&sort-order=1&keywords=N787BK&keywords-type=registration&keywords-contain=0 HTTP/1.1
Host: 127.0.0.1:8787`}
              />

              <div className="mt-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">cURL Example</h3>
                <CodeBlock
                  language="curl"
                  id="endpoint-curl"
                  code={`curl -X GET "http://127.0.0.1:8787/?page=1&sort-order=1&keywords=N787BK&keywords-type=registration&keywords-contain=0" \\
  -H "Accept: application/json"`}
                />
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">JavaScript (fetch)</h3>
                <CodeBlock
                  language="JavaScript"
                  id="endpoint-js-fetch"
                  code={`const params = new URLSearchParams({
  'page': '1',
  'sort-order': '1',
  'keywords': 'N787BK',
  'keywords-type': 'registration',
  'keywords-contain': '0'
});

fetch(\`http://127.0.0.1:8787/?\${params}\`)
  .then(res => res.json())
  .then(data => console.log(data))
  .catch(err => console.error(err));`}
                />
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Python (requests)</h3>
                <CodeBlock
                  language="Python"
                  id="endpoint-python"
                  code={`import requests

params = {
    'page': 1,
    'sort-order': 1,
    'keywords': 'N787BK',
    'keywords-type': 'registration',
    'keywords-contain': 0
}

response = requests.get('http://127.0.0.1:8787/', params=params)
data = response.json()
print(data)`}
                />
              </div>
            </div>
          )}

          {/* Parameters */}
          {activeSection === 'parameters' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Query Parameters</h2>

              <p className="text-gray-700 mb-6">
                All query parameters are passed via URL query string. The API accepts the following parameters:
              </p>

              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Parameter
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Default
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    <ParamRow
                      name="page"
                      type="integer"
                      required={true}
                      defaultValue={null}
                      description="Page number for pagination results"
                      examples={['1', '2', '10']}
                    />
                    <ParamRow
                      name="sort-order"
                      type="string"
                      required={true}
                      defaultValue={null}
                      description="Sort order for results"
                      examples={['0 (Recent)', '1 (Views)', '2 (Likes)']}
                    />
                    <ParamRow
                      name="keywords"
                      type="string"
                      required={false}
                      defaultValue='""'
                      description="Search keywords"
                      examples={['Boeing 747', 'N123AA', 'John Smith']}
                    />
                    <ParamRow
                      name="keywords-type"
                      type="string"
                      required={false}
                      defaultValue="all"
                      description="Field to search keywords in"
                      examples={['all', 'aircraft', 'registration', 'photographer']}
                    />
                    <ParamRow
                      name="keywords-contain"
                      type="string"
                      required={false}
                      defaultValue="3"
                      description="Keyword match type"
                      examples={['0 (exact)', '1 (starts)', '2 (ends)', '3 (contains)']}
                    />
                    <ParamRow
                      name="aircraft"
                      type="string"
                      required={false}
                      defaultValue="all"
                      description="Filter by aircraft model"
                      examples={['Airbus A320', 'Boeing 777-300ER', 'all']}
                    />
                    <ParamRow
                      name="airline"
                      type="string"
                      required={false}
                      defaultValue="all"
                      description="Filter by operating airline"
                      examples={['Delta Air Lines', 'Emirates', 'all']}
                    />
                    <ParamRow
                      name="country"
                      type="string"
                      required={false}
                      defaultValue="all"
                      description="Filter by country or location"
                      examples={['London Heathrow', 'United States', 'all']}
                    />
                    <ParamRow
                      name="year"
                      type="string"
                      required={false}
                      defaultValue="all"
                      description="Filter by photo year"
                      examples={['2023', '2024', 'all']}
                    />
                    <ParamRow
                      name="photographer"
                      type="string"
                      required={false}
                      defaultValue="all"
                      description="Filter by photographer name"
                      examples={['John Doe', 'all']}
                    />
                    <ParamRow
                      name="width"
                      type="integer"
                      required={false}
                      defaultValue={null}
                      description="Minimum image width in pixels"
                      examples={['1024', '1920', '3840']}
                    />
                    <ParamRow
                      name="height"
                      type="integer"
                      required={false}
                      defaultValue={null}
                      description="Minimum image height in pixels"
                      examples={['768', '1080', '2160']}
                    />
                  </tbody>
                </table>
              </div>

              <div className="mt-8 bg-blue-50 border-l-4 border-blue-500 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700 font-semibold">Parameter Encoding</p>
                    <p className="text-sm text-blue-700 mt-1">
                      Remember to URL-encode parameter values that contain spaces or special characters.
                      For example, "Boeing 747" should be encoded as "Boeing%20747".
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Responses */}
          {activeSection === 'responses' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Response Format</h2>

              <p className="text-gray-700 mb-6">
                The API returns JSON responses with the following structure:
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Success Response</h3>
              <CodeBlock
                language="JSON"
                id="response-success"
                code={`{
  "photos": [
    {
      "photoId": "12345678",
      "thumbnailUrl": "https://cdn.jetphotos.com/400/6/12345678.jpg",
      "imageUrl": "https://cdn.jetphotos.com/full/6/12345678.jpg",
      "photoPageUrl": "https://www.jetphotos.com/photo/12345678",
      "registration": "N787BK",
      "registrationUrl": "https://www.jetphotos.com/registration/N787BK",
      "aircraftType": "Boeing 787-8 Dreamliner",
      "airline": "United Airlines",
      "airlineUrl": "https://www.jetphotos.com/airline/United-Airlines",
      "photographer": "John Smith",
      "photographerUrl": "https://www.jetphotos.com/photographer/12345",
      "location": "Los Angeles International Airport",
      "locationUrl": "https://www.jetphotos.com/airport/KLAX",
      "photoDate": "2024-03-15",
      "uploadedDate": "2024-03-16",
      "likes": "142",
      "comments": "23",
      "views": "5847"
    }
  ],
  "count": 1,
  "meta": {
    "free": true,
    "mode": "workerd-direct",
    "provider": "local-workerd",
    "cached": false,
    "cacheTtlSeconds": 21600,
    "targetUrl": "https://www.jetphotos.com/photo/search?..."
  }
}`}
              />

              <div className="mt-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Response Fields</h3>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Field
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr>
                        <td className="px-6 py-4"><code className="text-sm text-indigo-600">count</code></td>
                        <td className="px-6 py-4"><code className="text-sm text-gray-700">integer</code></td>
                        <td className="px-6 py-4 text-sm text-gray-700">Total number of photos returned</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4"><code className="text-sm text-indigo-600">photos</code></td>
                        <td className="px-6 py-4"><code className="text-sm text-gray-700">array</code></td>
                        <td className="px-6 py-4 text-sm text-gray-700">Array of photo objects</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4"><code className="text-sm text-indigo-600">photoId</code></td>
                        <td className="px-6 py-4"><code className="text-sm text-gray-700">string</code></td>
                        <td className="px-6 py-4 text-sm text-gray-700">Unique photo identifier</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4"><code className="text-sm text-indigo-600">thumbnailUrl</code></td>
                        <td className="px-6 py-4"><code className="text-sm text-gray-700">string</code></td>
                        <td className="px-6 py-4 text-sm text-gray-700">URL to thumbnail image</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4"><code className="text-sm text-indigo-600">imageUrl</code></td>
                        <td className="px-6 py-4"><code className="text-sm text-gray-700">string</code></td>
                        <td className="px-6 py-4 text-sm text-gray-700">URL to full-size image</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4"><code className="text-sm text-indigo-600">photoPageUrl</code></td>
                        <td className="px-6 py-4"><code className="text-sm text-gray-700">string</code></td>
                        <td className="px-6 py-4 text-sm text-gray-700">URL to photo page on JetPhotos.com</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4"><code className="text-sm text-indigo-600">registration</code></td>
                        <td className="px-6 py-4"><code className="text-sm text-gray-700">string</code></td>
                        <td className="px-6 py-4 text-sm text-gray-700">Aircraft registration number</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4"><code className="text-sm text-indigo-600">aircraftType</code></td>
                        <td className="px-6 py-4"><code className="text-sm text-gray-700">string</code></td>
                        <td className="px-6 py-4 text-sm text-gray-700">Full aircraft type/model name</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4"><code className="text-sm text-indigo-600">airline</code></td>
                        <td className="px-6 py-4"><code className="text-sm text-gray-700">string</code></td>
                        <td className="px-6 py-4 text-sm text-gray-700">Operating airline name</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4"><code className="text-sm text-indigo-600">photographer</code></td>
                        <td className="px-6 py-4"><code className="text-sm text-gray-700">string</code></td>
                        <td className="px-6 py-4 text-sm text-gray-700">Photographer name</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4"><code className="text-sm text-indigo-600">location</code></td>
                        <td className="px-6 py-4"><code className="text-sm text-gray-700">string</code></td>
                        <td className="px-6 py-4 text-sm text-gray-700">Photo location/airport</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4"><code className="text-sm text-indigo-600">photoDate</code></td>
                        <td className="px-6 py-4"><code className="text-sm text-gray-700">string</code></td>
                        <td className="px-6 py-4 text-sm text-gray-700">Date photo was taken (YYYY-MM-DD)</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4"><code className="text-sm text-indigo-600">likes</code></td>
                        <td className="px-6 py-4"><code className="text-sm text-gray-700">string</code></td>
                        <td className="px-6 py-4 text-sm text-gray-700">Number of likes</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4"><code className="text-sm text-indigo-600">comments</code></td>
                        <td className="px-6 py-4"><code className="text-sm text-gray-700">string</code></td>
                        <td className="px-6 py-4 text-sm text-gray-700">Number of comments</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4"><code className="text-sm text-indigo-600">views</code></td>
                        <td className="px-6 py-4"><code className="text-sm text-gray-700">string</code></td>
                        <td className="px-6 py-4 text-sm text-gray-700">Number of views</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4"><code className="text-sm text-indigo-600">meta</code></td>
                        <td className="px-6 py-4"><code className="text-sm text-gray-700">object</code></td>
                        <td className="px-6 py-4 text-sm text-gray-700">Runtime metadata about this response (see fields below)</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4"><code className="text-sm text-indigo-600">meta.free</code></td>
                        <td className="px-6 py-4"><code className="text-sm text-gray-700">boolean</code></td>
                        <td className="px-6 py-4 text-sm text-gray-700"><code>true</code> — confirms the free self-hosted runtime was used</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4"><code className="text-sm text-indigo-600">meta.mode</code></td>
                        <td className="px-6 py-4"><code className="text-sm text-gray-700">string</code></td>
                        <td className="px-6 py-4 text-sm text-gray-700">Always <code>"workerd-direct"</code> (the Wrangler/workerd runtime)</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4"><code className="text-sm text-indigo-600">meta.provider</code></td>
                        <td className="px-6 py-4"><code className="text-sm text-gray-700">string</code></td>
                        <td className="px-6 py-4 text-sm text-gray-700">Where the request ran, e.g. <code>"local-workerd"</code></td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4"><code className="text-sm text-indigo-600">meta.cached</code></td>
                        <td className="px-6 py-4"><code className="text-sm text-gray-700">boolean</code></td>
                        <td className="px-6 py-4 text-sm text-gray-700">Whether this result came from the built-in cache</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4"><code className="text-sm text-indigo-600">meta.targetUrl</code></td>
                        <td className="px-6 py-4"><code className="text-sm text-gray-700">string</code></td>
                        <td className="px-6 py-4 text-sm text-gray-700">The JetPhotos search URL that was scraped (useful when debugging)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Empty Results</h3>
                <p className="text-gray-700 mb-3">
                  When no photos match the search criteria, the API returns:
                </p>
                <CodeBlock
                  language="JSON"
                  id="response-empty"
                  code={`{
  "photos": [],
  "count": 0
}`}
                />
              </div>
            </div>
          )}

          {/* Errors */}
          {activeSection === 'errors' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Error Handling</h2>

              <p className="text-gray-700 mb-6">
                The API uses standard HTTP status codes to indicate success or failure of requests.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">HTTP Status Codes</h3>
              <div className="space-y-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="bg-green-100 text-green-800 text-sm font-semibold px-3 py-1 rounded">200</span>
                    <span className="font-semibold text-gray-900">OK</span>
                  </div>
                  <p className="text-gray-700 text-sm">Request successful. Response contains photo data and a <code>meta</code> block.</p>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="bg-red-100 text-red-800 text-sm font-semibold px-3 py-1 rounded">403</span>
                    <span className="font-semibold text-gray-900">Forbidden (most common failure)</span>
                  </div>
                  <p className="text-gray-700 text-sm">
                    JetPhotos/Cloudflare blocked the host IP of your instance. The response includes{' '}
                    <code>blocked: true</code> and a <code>hint</code>. Switch to a residential network, a different VPS
                    region, or run at home. A Workers <em>edge</em> deploy frequently hits this.
                  </p>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="bg-red-100 text-red-800 text-sm font-semibold px-3 py-1 rounded">502</span>
                    <span className="font-semibold text-gray-900">Bad Gateway</span>
                  </div>
                  <p className="text-gray-700 text-sm">Upstream returned a Cloudflare/anti-bot page instead of photo results. Treat like a 403 — the IP appears blocked.</p>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="bg-red-100 text-red-800 text-sm font-semibold px-3 py-1 rounded">500</span>
                    <span className="font-semibold text-gray-900">Internal Server Error</span>
                  </div>
                  <p className="text-gray-700 text-sm">Unexpected error in the API (e.g. a parse failure). The <code>details</code> field describes what happened.</p>
                </div>
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Error Response Format</h3>
                <p className="text-gray-700 mb-3">
                  Errors carry the same <code>free</code>/<code>mode</code> fields, plus context about the block:
                </p>
                <CodeBlock
                  language="JSON"
                  id="error-format"
                  code={`{
  "error": "Failed to fetch source data: 403 Forbidden",
  "free": true,
  "mode": "workerd-direct",
  "targetUrl": "https://www.jetphotos.com/photo/search?...",
  "blocked": true,
  "hint": "Cloudflare blocked this host IP. Try a residential network, another VPS IP/region, or run locally at home.",
  "details": "<!DOCTYPE html>..."
}`}
                />
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Best Practices</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="text-indigo-600 mr-2 mt-1">•</span>
                    <div>
                      <span className="font-semibold text-gray-900">Handle errors gracefully:</span>
                      <span className="text-gray-700"> Always check the HTTP status code and handle error responses appropriately.</span>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="text-indigo-600 mr-2 mt-1">•</span>
                    <div>
                      <span className="font-semibold text-gray-900">Implement retry logic:</span>
                      <span className="text-gray-700"> For 5xx errors, implement exponential backoff retry strategies.</span>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="text-indigo-600 mr-2 mt-1">•</span>
                    <div>
                      <span className="font-semibold text-gray-900">Handle blocks (403/502):</span>
                      <span className="text-gray-700"> If <code>blocked</code> is <code>true</code>, your instance's IP is blocked — switch networks or regions rather than retrying in a tight loop.</span>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="text-indigo-600 mr-2 mt-1">•</span>
                    <div>
                      <span className="font-semibold text-gray-900">Validate parameters:</span>
                      <span className="text-gray-700"> Validate query parameters client-side before making requests to avoid 400 errors.</span>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Example Error Handling</h3>
                <CodeBlock
                  language="JavaScript"
                  id="error-handling-example"
                  code={`async function searchPhotos(params) {
  const base = 'http://127.0.0.1:8787';
  const response = await fetch(\`\${base}/?\${new URLSearchParams(params)}\`);

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    if (response.status === 403 || response.status === 502 || err.blocked) {
      // Your instance's IP is blocked by JetPhotos/Cloudflare.
      throw new Error(err.hint || 'Blocked by Cloudflare — try another network/IP.');
    }
    throw new Error(\`HTTP \${response.status}: \${err.error || response.statusText}\`);
  }

  return response.json();
}`}
                />
              </div>
            </div>
          )}

          {/* Source Code */}
          {activeSection === 'sourcecode' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Source Code</h2>

              <p className="text-gray-700 mb-6">
                The JetPhotos API is open source and available under the MIT License. You can download, modify,
                and deploy your own instance of the API.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <a
                  href="https://github.com/roowus/JetPhotos-API"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-gradient-to-r from-gray-900 to-gray-800 text-white p-6 rounded-lg hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xl font-semibold">GitHub Repository</h3>
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-300 mb-4">
                    View the source code, contribute, or report issues on GitHub.
                  </p>
                  <div className="flex items-center text-sm text-indigo-300">
                    <span>Visit Repository</span>
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </a>

                <a
                  href="https://github.com/roowus/JetPhotos-API/releases/latest"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-gradient-to-r from-indigo-600 to-indigo-700 text-white p-6 rounded-lg hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xl font-semibold">Latest Release</h3>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                    </svg>
                  </div>
                  <p className="text-sm text-indigo-100 mb-4">
                    Download the latest stable release with full source code and deployment instructions.
                  </p>
                  <div className="flex items-center text-sm text-indigo-200">
                    <span>Download Release</span>
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </a>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">What's Included</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <span className="font-semibold text-gray-900">worker.js</span>
                      <span className="text-gray-700"> - The API and JetPhotos HTML parser (runs on the free workerd runtime)</span>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <span className="font-semibold text-gray-900">wrangler.toml</span>
                      <span className="text-gray-700"> - Local + deploy configuration (sets <code>CACHE_TTL_SECONDS</code>)</span>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <span className="font-semibold text-gray-900">scripts/smoke-test.mjs</span>
                      <span className="text-gray-700"> - Automated health + search check (<code>npm run test:api</code>)</span>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <span className="font-semibold text-gray-900">README.md</span>
                      <span className="text-gray-700"> - Comprehensive documentation and hosting guide</span>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <span className="font-semibold text-gray-900">LICENSE</span>
                      <span className="text-gray-700"> - MIT License - free to use and modify</span>
                    </div>
                  </li>
                </ul>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Quick Clone</h3>
              <CodeBlock
                language="bash"
                id="clone-repo"
                code={`git clone https://github.com/roowus/JetPhotos-API.git
cd JetPhotos-API
npm install
npm start    # → http://127.0.0.1:8787`}
              />

              <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      <span className="font-semibold">Want to expose it publicly?</span> See the{' '}
                      <button onClick={() => setActiveSection('hosting')} className="underline font-semibold">
                        Hosting
                      </button>{' '}
                      section for VPS, tunnel, and (optional) edge-deploy instructions, or read the{' '}
                      <a href="https://github.com/roowus/JetPhotos-API#hosting" target="_blank" rel="noopener noreferrer" className="underline">
                        README
                      </a>.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Hosting */}
          {activeSection === 'hosting' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Hosting</h2>

              <p className="text-gray-700 mb-6">
                You run this API yourself, so success depends on the <strong>network path</strong> to JetPhotos — not on
                any paid service. JetPhotos is Cloudflare-protected, so where your instance lives matters.
              </p>

              <div className="overflow-x-auto border border-gray-200 rounded-lg mb-8">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Where you run it</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Free?</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usually works?</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">Your computer</td>
                      <td className="px-6 py-4 text-sm text-green-700 font-semibold">Yes</td>
                      <td className="px-6 py-4 text-sm text-green-700 font-semibold">Yes</td>
                      <td className="px-6 py-4 text-sm text-gray-700">Best option. Uses your residential network.</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">VPS</td>
                      <td className="px-6 py-4 text-sm text-gray-700">VPS may cost</td>
                      <td className="px-6 py-4 text-sm text-yellow-700 font-semibold">Maybe</td>
                      <td className="px-6 py-4 text-sm text-gray-700">Some datacenter IPs get a Cloudflare <code>403</code>. Test first.</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">Workers edge deploy</td>
                      <td className="px-6 py-4 text-sm text-green-700 font-semibold">Yes</td>
                      <td className="px-6 py-4 text-sm text-red-700 font-semibold">Often no</td>
                      <td className="px-6 py-4 text-sm text-gray-700">JetPhotos blocks many Worker edge requests.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">A) Local / home machine (recommended)</h3>
              <CodeBlock
                language="bash"
                id="hosting-local"
                code={`npm install
npm start`}
              />
              <p className="text-gray-700 mt-3 mb-3">Keep it running with a process manager:</p>
              <CodeBlock
                language="bash"
                id="hosting-pm2"
                code={`npm install -g pm2
pm2 start npm --name jetphotos-api -- start
pm2 save`}
              />

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Public URL from home (free tunnel)</h3>
              <CodeBlock
                language="bash"
                id="hosting-tunnel"
                code={`# terminal 1
npm start

# terminal 2
cloudflared tunnel --url http://127.0.0.1:8787`}
              />
              <p className="text-gray-700 mt-3">
                Use the resulting <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">https://&lt;random&gt;.trycloudflare.com</code> URL as your public API.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">B) VPS (Hetzner, DigitalOcean, Oracle Free Tier…)</h3>
              <CodeBlock
                language="bash"
                id="hosting-vps"
                code={`sudo apt update
sudo apt install -y git nodejs npm
git clone https://github.com/roowus/Jetphotos-API.git
cd Jetphotos-API
npm install
npm start    # binds 0.0.0.0:8787`}
              />
              <p className="text-gray-700 mt-3">
                Put Nginx + Certbot (HTTPS) in front and persist with <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">pm2 startup</code>.
                If the VPS IP earns a <code>403</code>, try another region/provider or run at home.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">C) Cloudflare Workers edge (optional, often blocked)</h3>
              <CodeBlock
                language="bash"
                id="hosting-edge"
                code={`npm run deploy    # → https://jetphotos-api.<subdomain>.workers.dev`}
              />
              <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-500 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      Expect a possible <code>403</code> — JetPhotos frequently blocks Cloudflare edge IPs. The code is identical;
                      only the network path changes. If it's blocked, use local or VPS hosting instead.
                    </p>
                  </div>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Environment</h3>
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Default</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Meaning</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4"><code className="text-sm text-indigo-600">CACHE_TTL_SECONDS</code></td>
                      <td className="px-6 py-4"><code className="text-sm text-gray-700">21600</code></td>
                      <td className="px-6 py-4 text-sm text-gray-700">How long successful JSON is cached (6 hours). Set in <code>wrangler.toml</code> <code>[vars]</code>.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Troubleshooting</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Failed to fetch source data: 403 Forbidden</p>
                  <p className="text-gray-700 text-sm mt-1">Cloudflare blocked the host IP. Run locally at home, or move the VPS region/provider. Edge <code>*.workers.dev</code> is the least likely to work.</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Local <code>npm start</code> works but deploy does not</p>
                  <p className="text-gray-700 text-sm mt-1">Expected. Keep using self-host (local/VPS/tunnel) and don't rely on edge deploy for JetPhotos.</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm"><code>count: 0</code></p>
                  <p className="text-gray-700 text-sm mt-1">The search may genuinely be empty, or JetPhotos' HTML markup changed. Inspect <code>meta.targetUrl</code> in a browser.</p>
                </div>
              </div>
            </div>
          )}

          {/* Query Builder */}
          {activeSection === 'querybuilder' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Query Builder</h2>

              <p className="text-gray-700 mb-6">
                Build and test API queries interactively. Fill in the parameters below to generate a complete API URL
                that you can use in your applications.
              </p>

              <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Query Parameters</h3>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    API Base URL <Badge variant="required">Required</Badge>
                  </label>
                  <input
                    type="text"
                    value={queryParams.baseUrl}
                    onChange={(e) => setQueryParams({...queryParams, baseUrl: e.target.value})}
                    placeholder="http://127.0.0.1:8787"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Where your self-hosted instance runs. Use <code>http://127.0.0.1:8787</code> for local, or your VPS/tunnel URL.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Page <Badge variant="required">Required</Badge>
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={queryParams.page}
                      onChange={(e) => setQueryParams({...queryParams, page: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sort Order <Badge variant="required">Required</Badge>
                    </label>
                    <select
                      value={queryParams.sortOrder}
                      onChange={(e) => setQueryParams({...queryParams, sortOrder: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="0">Recent</option>
                      <option value="1">Views</option>
                      <option value="2">Likes</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Keywords <Badge variant="optional">Optional</Badge>
                    </label>
                    <input
                      type="text"
                      value={queryParams.keywords}
                      onChange={(e) => setQueryParams({...queryParams, keywords: e.target.value})}
                      placeholder="e.g., Boeing 747, N123AA"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Keywords Type <Badge variant="optional">Optional</Badge>
                    </label>
                    <select
                      value={queryParams.keywordsType}
                      onChange={(e) => setQueryParams({...queryParams, keywordsType: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="all">All</option>
                      <option value="aircraft">Aircraft</option>
                      <option value="registration">Registration</option>
                      <option value="photographer">Photographer</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Keywords Contain <Badge variant="optional">Optional</Badge>
                    </label>
                    <select
                      value={queryParams.keywordsContain}
                      onChange={(e) => setQueryParams({...queryParams, keywordsContain: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="0">Exact</option>
                      <option value="1">Starts with</option>
                      <option value="2">Ends with</option>
                      <option value="3">Contains</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Aircraft <Badge variant="optional">Optional</Badge>
                    </label>
                    <input
                      type="text"
                      value={queryParams.aircraft === 'all' ? '' : queryParams.aircraft}
                      onChange={(e) => setQueryParams({...queryParams, aircraft: e.target.value || 'all'})}
                      placeholder="e.g., Airbus A320"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Airline <Badge variant="optional">Optional</Badge>
                    </label>
                    <input
                      type="text"
                      value={queryParams.airline === 'all' ? '' : queryParams.airline}
                      onChange={(e) => setQueryParams({...queryParams, airline: e.target.value || 'all'})}
                      placeholder="e.g., Delta Air Lines"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Country/Location <Badge variant="optional">Optional</Badge>
                    </label>
                    <input
                      type="text"
                      value={queryParams.country === 'all' ? '' : queryParams.country}
                      onChange={(e) => setQueryParams({...queryParams, country: e.target.value || 'all'})}
                      placeholder="e.g., London Heathrow"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Year <Badge variant="optional">Optional</Badge>
                    </label>
                    <input
                      type="text"
                      value={queryParams.year === 'all' ? '' : queryParams.year}
                      onChange={(e) => setQueryParams({...queryParams, year: e.target.value || 'all'})}
                      placeholder="e.g., 2024"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Photographer <Badge variant="optional">Optional</Badge>
                    </label>
                    <input
                      type="text"
                      value={queryParams.photographer === 'all' ? '' : queryParams.photographer}
                      onChange={(e) => setQueryParams({...queryParams, photographer: e.target.value || 'all'})}
                      placeholder="e.g., John Doe"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Min Width (px) <Badge variant="optional">Optional</Badge>
                    </label>
                    <input
                      type="number"
                      value={queryParams.width}
                      onChange={(e) => setQueryParams({...queryParams, width: e.target.value})}
                      placeholder="e.g., 1920"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Min Height (px) <Badge variant="optional">Optional</Badge>
                    </label>
                    <input
                      type="number"
                      value={queryParams.height}
                      onChange={(e) => setQueryParams({...queryParams, height: e.target.value})}
                      placeholder="e.g., 1080"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Generated URL</h3>

                {(() => {
                  const params = new URLSearchParams();
                  params.set('page', queryParams.page);
                  params.set('sort-order', queryParams.sortOrder);
                  if (queryParams.keywords) params.set('keywords', queryParams.keywords);
                  if (queryParams.keywordsType !== 'all') params.set('keywords-type', queryParams.keywordsType);
                  if (queryParams.keywordsContain !== '3') params.set('keywords-contain', queryParams.keywordsContain);
                  if (queryParams.aircraft !== 'all') params.set('aircraft', queryParams.aircraft);
                  if (queryParams.airline !== 'all') params.set('airline', queryParams.airline);
                  if (queryParams.country !== 'all') params.set('country', queryParams.country);
                  if (queryParams.year !== 'all') params.set('year', queryParams.year);
                  if (queryParams.photographer !== 'all') params.set('photographer', queryParams.photographer);
                  if (queryParams.width) params.set('width', queryParams.width);
                  if (queryParams.height) params.set('height', queryParams.height);

                  const base = (queryParams.baseUrl || 'http://127.0.0.1:8787').replace(/\/$/, '');
                  const fullUrl = `${base}/?${params.toString()}`;

                  return (
                    <>
                      <div className="bg-white border border-gray-300 rounded-md p-4 mb-4">
                        <code className="text-sm text-gray-800 break-all">{fullUrl}</code>
                      </div>

                      <div className="flex gap-3">
                        <button
                          onClick={() => copyToClipboard(fullUrl, 'query-url')}
                          className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                        >
                          {copiedCode === 'query-url' ? 'Copied!' : 'Copy URL'}
                        </button>
                        <a
                          href={fullUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                        >
                          Test in Browser →
                        </a>
                      </div>
                    </>
                  );
                })()}
              </div>

              <div className="mt-6 bg-yellow-50 border-l-4 border-yellow-500 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      The generated URL targets <strong>your own</strong> self-hosted instance. Make sure you've started
                      it with <code>npm start</code> (or that it's reachable at the Base URL you entered). If the browser
                      can't load the result, the instance may be down or that IP is blocked by Cloudflare.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </main>
    </div>
  );
}

export default App;
